import {
  FetchResult,
  gql,
  MutationFunctionOptions,
  useLazyQuery,
} from "@apollo/client";
import { RichBlockProviderOrganization } from "client/guide-content/rich-blocks/provider";
import {
  AvailabilityRequestFragment,
  ContextForRequestAvailabilityCalendar,
  ContextForRequestAvailabilityCalendarVariables,
  GuidePostForRequestAvailabilityCalendar,
  GuidePostForRequestAvailabilityCalendarVariables,
  SaveCandidateAvailability,
  SaveCandidateAvailabilityVariables,
  UpdateAvailabilityRequest,
  UpdateAvailabilityRequestVariables,
} from "generated/schemaTypes";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import useMutation from "utils/useMutation";

const AVAILABILITY_REQUEST_CALENDAR_EVENT_FRAGMENT = gql`
  fragment AvailabilityRequestCalendarEventFragment on CalendarEvent {
    id
    title
    start
    end
    allDay
    createdBy {
      id
    }
  }
`;

const AVAILABILITY_REQUEST_FRAGMENT = gql`
  fragment AvailabilityRequestFragment on AvailabilityRequest {
    id
    title
    description
    createMultipleEventsOnDrag
    forceResponsesInIncrements
    overlayCalendars
    calendars
    allowSelectionOutsideSuggestions
    calendarEvents {
      ...AvailabilityRequestCalendarEventFragment
    }
    candidateAvailabilities {
      id
    }
  }
  ${AVAILABILITY_REQUEST_CALENDAR_EVENT_FRAGMENT}
`;

const CONTEXT_FOR_REQUEST_AVAILABILITY_CALENDAR = gql`
  query ContextForRequestAvailabilityCalendar($where: GuideWhereUniqueInput!) {
    guideFindUnique(where: $where) {
      id
      candidate {
        id
        name
        organization {
          id
          defaultAvatarImageUrl
          name
          companyLogoUrl
        }
      }
    }
  }
`;

const GUIDE_POST_FOR_REQUEST_AVAILABILITY_CALENDAR = gql`
  query GuidePostForRequestAvailabilityCalendar($guidePostId: String!) {
    guidePostById(id: $guidePostId) {
      id
      parent {
        id
      }
      availabilityRequests {
        ...AvailabilityRequestFragment
      }
    }
  }
  ${AVAILABILITY_REQUEST_FRAGMENT}
`;

const UPDATE_AVAILABILITY_REQUEST = gql`
  mutation UpdateAvailabilityRequest($input: UpdateAvailabilityRequestInput!) {
    updateAvailabilityRequest(input: $input) {
      success
      code
      message
      availabilityRequest {
        id
        ...AvailabilityRequestFragment
      }
    }
  }
  ${AVAILABILITY_REQUEST_FRAGMENT}
`;

const SAVE_CANDIDATE_AVAILABILITY = gql`
  mutation SaveCandidateAvailability($input: CreateGuidePostInput!) {
    createGuidePost(input: $input) {
      success
      code
      message
      guidePost {
        id
        threadId
        parent {
          id
          replies {
            id
          }
        }
        guide {
          id
          latestPost {
            id
          }
        }
      }
    }
  }
`;

export type RequestAvailabilityRichBlockContextValue = {
  guideId?: string;
  guidePostId?: string;
  guidePostThreadId?: string;
  availabilityRequests: AvailabilityRequestFragment[];
  updateAvailabilityRequest: (
    options?: MutationFunctionOptions<
      UpdateAvailabilityRequest,
      UpdateAvailabilityRequestVariables
    >
  ) => Promise<FetchResult<UpdateAvailabilityRequest>>;
  saveCandidateAvailability: (
    options?: MutationFunctionOptions<
      SaveCandidateAvailability,
      SaveCandidateAvailabilityVariables
    >
  ) => Promise<FetchResult<SaveCandidateAvailability>>;
  getAvailabilityRequestById: (
    id: string
  ) => AvailabilityRequestFragment | undefined;
  organization?: {
    name: string;
    avatarUrl: string;
    companyLogoUrl: string;
  };
  candidate?: {
    name: string;
  };
  loading: boolean;
  fetchRequiredData: () => void;
};
const RequestAvailabilityRichBlockContext = createContext<
  RequestAvailabilityRichBlockContextValue | undefined
>(undefined);

export function useRequestAvailability() {
  const contextValue = useContext(RequestAvailabilityRichBlockContext);
  useEffect(() => {
    // Fetch required data only when the context is requested which will only happen inside the relevant rich block components
    contextValue?.fetchRequiredData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!contextValue) {
    throw new Error(
      "useRequestAvailability can only be called inside a RequestAvailabilityRichBlockProvider"
    );
  }

  return contextValue;
}

export function RequestAvailabilityRichBlockProvider({
  children,
  guidePostId,
  guideId,
  organization,
}: {
  children: ReactNode;
  guideId?: string;
  guidePostId?: string;
  organization?: RichBlockProviderOrganization | null;
}) {
  const [
    getContextForRequestAvailabilityCalendar,
    { called: calledGetContext, data: contextData, loading: contextLoading },
  ] = useLazyQuery<
    ContextForRequestAvailabilityCalendar,
    ContextForRequestAvailabilityCalendarVariables
  >(CONTEXT_FOR_REQUEST_AVAILABILITY_CALENDAR);

  const [
    getGuidePostForRequestAvailability,
    { called: calledGetGuidePost, data, loading: guidePostLoading },
  ] = useLazyQuery<
    GuidePostForRequestAvailabilityCalendar,
    GuidePostForRequestAvailabilityCalendarVariables
  >(GUIDE_POST_FOR_REQUEST_AVAILABILITY_CALENDAR);

  const fetchRequiredData = useCallback(() => {
    if (!calledGetContext && guideId) {
      getContextForRequestAvailabilityCalendar({
        variables: {
          where: {
            id: guideId,
          },
        },
      });
    }
    if (!calledGetGuidePost && guidePostId) {
      getGuidePostForRequestAvailability({
        variables: {
          guidePostId,
        },
      });
    }
  }, [
    calledGetContext,
    calledGetGuidePost,
    guideId,
    guidePostId,
    getContextForRequestAvailabilityCalendar,
    getGuidePostForRequestAvailability,
  ]);

  const guidePost = data?.guidePostById;

  const availabilityRequests = useMemo(
    () => data?.guidePostById?.availabilityRequests ?? [],
    [data?.guidePostById?.availabilityRequests]
  );

  const candidate = contextData?.guideFindUnique.candidate;
  const currentOrganization = candidate?.organization ?? organization;

  const [updateAvailabilityRequest] = useMutation<
    UpdateAvailabilityRequest,
    UpdateAvailabilityRequestVariables
  >(UPDATE_AVAILABILITY_REQUEST);

  const [saveCandidateAvailability] = useMutation<
    SaveCandidateAvailability,
    SaveCandidateAvailabilityVariables
  >(SAVE_CANDIDATE_AVAILABILITY);

  const getAvailabilityRequestById = useCallback(
    (id: string) =>
      availabilityRequests.find(
        (availabilityRequest) => availabilityRequest.id === id
      ),
    [availabilityRequests]
  );

  return (
    <RequestAvailabilityRichBlockContext.Provider
      value={{
        guideId,
        guidePostId,
        guidePostThreadId: guidePost?.parent?.id ?? guidePostId,
        candidate: candidate
          ? {
              name: candidate.name,
            }
          : undefined,
        organization: currentOrganization
          ? {
              avatarUrl: currentOrganization.defaultAvatarImageUrl,
              name: currentOrganization.name,
              companyLogoUrl: currentOrganization.companyLogoUrl,
            }
          : undefined,
        availabilityRequests,
        updateAvailabilityRequest,
        saveCandidateAvailability,
        getAvailabilityRequestById,
        loading: contextLoading || guidePostLoading,
        fetchRequiredData,
      }}
    >
      {children}
    </RequestAvailabilityRichBlockContext.Provider>
  );
}

RequestAvailabilityRichBlockProvider.fragments = {
  availabilityRequest: AVAILABILITY_REQUEST_FRAGMENT,
};
